import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalAccountRoute from "@faire/web-api--source/routes/brand-portal/account";
import React, { type JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const PayoutNotReceivedIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="If you’re experiencing a delay in receiving your payout, it’s essential to verify a few key details about your order and account settings to understand why. Here’s what to check:"
          description={{
            img: "https://cdn.faire.com/fastly/52a126e9dd2e8c7e631a8e5405106c9fa31adba43dfc9ab943515b30c2ae5c54.png",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage='<BoldFormatting>Order Status:</BoldFormatting> Your order must be marked as "Shipped" to trigger a payout. Ensure you’ve entered shipment tracking information so that the status is accurate.'
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              }}
              description={{
                text: "First step of the list in how to receive payments",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="<BoldFormatting>Payout Options:</BoldFormatting> Review the <LinkFormatting>payout terms</LinkFormatting> you’ve selected. Payment will be initiated up to 60 days after shipment, depending on the terms you choose. It typically takes  3-5 business days to reflect in your account, depending on your bank’s processing time."
              description={{
                text: "Second step of the list in how to receive payments",
              }}
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
                LinkFormatting: (chunks: JSX.Element) =>
                  PayoutsLinkFormatting(chunks, "payment"),
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="<BoldFormatting>Bank Account Information:</BoldFormatting> Double check that your bank account details are accurate and up-to-date. Incorrect information can lead to payout delays."
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              }}
              description={{
                text: "Third step of the list in how to receive payments",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage='<BoldFormatting>Preorders:</BoldFormatting> For preorders, both Payout and Ship dates might be listed as "Unknown" until the preorder fulfillment is complete. Please wait for these orders to be fulfilled before expecting a payout.'
              values={{
                BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
              }}
              description={{
                text: "Fourth step of the list in how to receive payments",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
    </Column>
  );
};

const PayoutsLinkFormatting = (chunks: JSX.Element, tab: string) => (
  <Button
    variant="tertiary"
    linkTo={brandPortalAccountRoute({ tab })}
    target="_blank"
  >
    <Bold>{chunks}</Bold>
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
